import React, { useState, useRef, useEffect } from 'react';
import {
	Dialog,
	DialogContent,
	Box,
	Typography,
	IconButton,
	Avatar,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Button,
	Paper,
	Fade,
	Tooltip,
	Zoom,
	Menu,
	MenuItem,
	DialogTitle,
	DialogActions,
	DialogContentText,
	TextField,
	Skeleton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import AIAssistantInput from '../molecules/AIAssitantInput';
import AnaAvatar from '../../../pages/Ana/AnaAvatar';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import {
	archiveConversation,
	createConversation,
	deleteConversation,
	readArchivedConversation,
	readConversation,
	readSingleConversation,
	resetAIAssistant,
	sendFeedback,
	updateRecentArchivedConversation,
	updateRecentConversation
} from '../../../redux/slices/ai-assistant';
import { RequestStatus } from '../../../redux/utils';
import { RecentConversation, Conversation, AgentType } from '../../../types/ai-assistant';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { enqueueSnackbar } from 'notistack';
import { Buffer } from 'buffer';
import { useTranslation } from 'react-i18next';

const WaterAnimation = () => {
	return (
		<Box
			sx={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				overflow: 'hidden',
				zIndex: -1,
				opacity: 0.1,
				'&::before, &::after': {
					content: '""',
					position: 'absolute',
					width: '200%',
					height: '200%',
					top: '-50%',
					left: '-50%',
					background: 'radial-gradient(ellipse at center, rgba(0,123,255,0.2) 0%, rgba(0,123,255,0) 70%)',
					animation: 'wave 15s linear infinite',
					borderRadius: '40%'
				},
				'&::after': {
					animation: 'wave 15s linear infinite reverse',
					opacity: 0.5,
					animationDelay: '-5s'
				},
				'@keyframes wave': {
					'0%': {
						transform: 'rotate(0deg)'
					},
					'100%': {
						transform: 'rotate(360deg)'
					}
				}
			}}
		/>
	);
};

interface PulsingAvatarProps {
	src?: string;
	children?: React.ReactNode;
	sx?: any;
	[key: string]: any;
}

const PulsingAvatar: React.FC<PulsingAvatarProps> = ({ src, children, sx, ...props }) => {
	return (
		<Box sx={{ position: 'relative', width: sx?.width, height: sx?.height }}>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					borderRadius: '50%',
					animation: 'pulse 2s infinite',
					backgroundColor: 'rgba(33, 150, 243, 0.3)',
					'@keyframes pulse': {
						'0%': {
							transform: 'scale(1)',
							opacity: 0.7
						},
						'50%': {
							transform: 'scale(1.2)',
							opacity: 0.3
						},
						'100%': {
							transform: 'scale(1)',
							opacity: 0.7
						}
					}
				}}
			/>
			<Avatar src={src} sx={sx} {...props}>
				{children}
			</Avatar>
		</Box>
	);
};

const TypingText = ({ text, speed = 30, onComplete }: { text: string; speed?: number; onComplete?: () => void }) => {
	const [displayedText, setDisplayedText] = useState('');
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isComplete, setIsComplete] = useState(false);

	useEffect(() => {
		if (currentIndex < text.length) {
			const timeout = setTimeout(() => {
				setDisplayedText((prev) => prev + text[currentIndex]);
				setCurrentIndex((prev) => prev + 1);
			}, speed);

			return () => clearTimeout(timeout);
		} else if (!isComplete) {
			setIsComplete(true);
			onComplete?.();
		}
	}, [currentIndex, text, speed, isComplete, onComplete]);

	useEffect(() => {
		setDisplayedText('');
		setCurrentIndex(0);
		setIsComplete(false);
	}, [text]);

	return (
		<>
			{displayedText}
			{!isComplete && <span className="typing-cursor">|</span>}
		</>
	);
};

interface AiAssistantProps {
	open: boolean;
	onClose: () => void;
}

interface MessageFeedbackProps {
	messageId: string;
	feedback: 'positive' | 'negative' | null;
	onFeedback: (messageId: string, type: 'positive' | 'negative' | null, comment?: string) => void;
	isMobile: boolean;
	forceShow?: boolean;
}

const MessageFeedback: React.FC<MessageFeedbackProps> = ({ messageId, feedback, onFeedback, isMobile }) => {
	const { t } = useTranslation();
	const [showCommentField, setShowCommentField] = useState(false);
	const [comment, setComment] = useState('');

	const handleNegativeFeedback = () => {
		onFeedback(messageId, 'negative');
		setShowCommentField(true);

		enqueueSnackbar(t('common.types.aiAssistant.notHelpfulMessage'), {
			variant: 'success',
			anchorOrigin: {
				vertical: 'bottom',
				horizontal: 'left'
			}
		});
	};

	const handleCommentSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			onFeedback(messageId, 'negative', comment);
			setShowCommentField(false);
			setComment('');

			enqueueSnackbar(t('common.types.aiAssistant.notHelpfulCommentMessage'), {
				variant: 'success',
				anchorOrigin: {
					vertical: 'bottom',
					horizontal: 'left'
				}
			});
		}
	};

	const handleCommentBlur = () => {
		if (comment.trim()) {
			onFeedback(messageId, 'negative', comment);
		}
		setShowCommentField(false);
		setComment('');
	};

	return (
		<Box
			className={isMobile ? 'mobile-feedback' : 'desktop-feedback'}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: 1,
				position: 'absolute',
				right: isMobile ? '50%' : 30,
				top: isMobile ? -45 : '50%',
				transform: isMobile ? 'translateX(50%)' : 'translateY(-50%)',
				backgroundColor: (theme: any) =>
					theme.palette.mode === 'dark' ? 'rgba(0, 30, 69, 0.95)' : 'rgba(255, 255, 255, 0.95)',
				borderRadius: 5,
				boxShadow: isMobile ? '0 3px 14px rgba(0, 0, 0, 0.2)' : '0 2px 10px rgba(0, 0, 0, 0.1)',
				padding: isMobile ? '6px 12px' : '4px 8px',
				paddingBottom: showCommentField ? '8px' : isMobile ? '6px' : '4px',
				zIndex: 10,
				opacity: isMobile ? 1 : 0,
				transition: 'all 0.2s ease-in-out',
				// Show on parent hover
				'.message-container:hover &.desktop-feedback': {
					opacity: 1
				},
				// Show on direct hover
				'&.desktop-feedback:hover': {
					opacity: 1
				},
				border: isMobile ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
				width: showCommentField ? (isMobile ? '200px' : '180px') : 'auto'
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
				<Tooltip title="Helpful" placement={isMobile ? 'top' : undefined}>
					<IconButton
						size="small"
						onClick={() => {
							onFeedback(messageId, 'positive');
							setShowCommentField(false);
						}}
						sx={{
							color: feedback === 'positive' ? 'success.main' : 'text.secondary',
							'&:hover': { color: 'success.main' },
							...(isMobile && {
								backgroundColor: feedback === 'positive' ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
							})
						}}
					>
						{feedback === 'positive' ? (
							<ThumbUpIcon fontSize={isMobile ? 'small' : 'small'} />
						) : (
							<ThumbUpAltOutlinedIcon fontSize={isMobile ? 'small' : 'small'} />
						)}
					</IconButton>
				</Tooltip>
				<Tooltip title="Not helpful" placement={isMobile ? 'top' : undefined}>
					<IconButton
						size="small"
						onClick={handleNegativeFeedback}
						sx={{
							color: feedback === 'negative' ? 'error.main' : 'text.secondary',
							'&:hover': { color: 'error.main' },
							...(isMobile && {
								backgroundColor: feedback === 'negative' ? 'rgba(244, 67, 54, 0.1)' : 'transparent'
							})
						}}
					>
						{feedback === 'negative' ? (
							<ThumbDownIcon fontSize={isMobile ? 'small' : 'small'} />
						) : (
							<ThumbDownAltOutlinedIcon fontSize={isMobile ? 'small' : 'small'} />
						)}
					</IconButton>
				</Tooltip>
			</Box>

			{showCommentField && (
				<>
					<TextField
						autoFocus
						size="small"
						variant="outlined"
						placeholder={t('common.types.aiAssistant.tellUsMore')}
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						onKeyDown={handleCommentSubmit}
						onBlur={handleCommentBlur}
						fullWidth
						sx={{
							mt: 0.5,
							'.MuiOutlinedInput-root': {
								borderRadius: 2,
								fontSize: '0.8rem'
							},
							'& .MuiOutlinedInput-input': {
								padding: '6px 8px'
							}
						}}
					/>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{
							fontSize: '0.7rem',
							mt: 0.5,
							textAlign: 'center',
							width: '100%',
							display: 'block'
						}}
					>
						{t('common.actions.pressEnterToSubmit', 'Press Enter to submit')}
					</Typography>
				</>
			)}
		</Box>
	);
};

// Custom code renderer component
const CodeBlock = (props: any) => {
	const { className, children } = props;
	const match = /language-(\w+)/.exec(className || '');
	return match ? (
		<SyntaxHighlighter style={atomDark as any} language={match[1]}>
			{String(children).replace(/\n$/, '')}
		</SyntaxHighlighter>
	) : (
		<code className={className} {...props}>
			{children}
		</code>
	);
};

const AiAssistant = ({ open, onClose }: AiAssistantProps) => {
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	const [input, setInput] = useState('');
	const [messages, setMessages] = useState<Conversation[]>([]);
	const [messageFeedback, setMessageFeedback] = useState<Record<string, 'positive' | 'negative' | null>>({});
	const [conversations, setConversations] = useState<RecentConversation[]>([]);
	const [archivedConversations, setArchivedConversations] = useState<RecentConversation[]>([]);
	const [currentConversation, setCurrentConversation] = useState<string | null>(null);
	const [isTyping, setIsTyping] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [showConversationList, setShowConversationList] = useState(true);
	const [showChatInterface, setShowChatInterface] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [isXs, setIsXs] = useState(false);
	const [pressedMessageId, setPressedMessageId] = useState<string | null>(null);
	const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	// Pagination state
	const [activeConversationsNextToken, setActiveConversationsNextToken] = useState<string | null>(null);
	const [archivedConversationsNextToken, setArchivedConversationsNextToken] = useState<string | null>(null);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
	const [showArchivedConversations, setShowArchivedConversations] = useState(false);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [confirmDialogAction, setConfirmDialogAction] = useState<'delete' | 'archive' | null>(null);

	const [
		user,
		recentConversations,
		recentConversationsRS,
		recentArchivedConversations,
		recentArchivedConversationsRS,
		singleConversation,
		singleConversationRS
	] = useAppSelector((state) => [
		state.user.currentUser,
		state.aiAssistant.recentConversations.chatAgent,
		state.aiAssistant.recentConversationRequest,
		state.aiAssistant.recentArchivedConversations,
		state.aiAssistant.recentArchivedConversationRequest,
		state.aiAssistant.singleConversation,
		state.aiAssistant.readSingleConversationRequest
	]);

	useEffect(() => {
		const checkScreenSize = () => {
			setIsMobile(window.innerWidth <= 768);
			setIsXs(window.innerWidth < 360);
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => {
			window.removeEventListener('resize', checkScreenSize);
		};
	}, []);

	useEffect(() => {
		if (user) {
			dispatch(
				readArchivedConversation({ userId: user.id, limit: 20, next: null, agentType: AgentType.CHAT_AGENT })
			);
			dispatch(readConversation({ userId: user.id, limit: 20, next: null, agentType: AgentType.CHAT_AGENT }));
		}
	}, [user, dispatch]);

	useEffect(() => {
		if (recentConversationsRS.status === RequestStatus.SUCCESS) {
			// Only update state if we're not currently loading more conversations
			// This prevents overriding our manual state updates in handleLoadMoreConversations
			if (!isLoadingMore) {
				const sortedConversations = [...(recentConversations?.items || [])].sort((a, b) => {
					return (
						new Date(b.lastMessageDateTime as Date).getTime() -
						new Date(a.lastMessageDateTime as Date).getTime()
					);
				});
				setConversations(sortedConversations);
			}

			// Always update the next token from the API response
			setActiveConversationsNextToken(recentConversations?.next || null);

			// Reset loading state if needed
			if (isLoadingMore) {
				setIsLoadingMore(false);
			}
		}
	}, [recentConversations, recentConversationsRS]);

	useEffect(() => {
		if (recentArchivedConversationsRS.status === RequestStatus.SUCCESS) {
			// Only update state if we're not currently loading more conversations
			// This prevents overriding our manual state updates in handleLoadMoreConversations
			if (!isLoadingMore) {
				const sortedArchivedConversations = [...(recentArchivedConversations?.items || [])].sort((a, b) => {
					return (
						new Date(b.lastMessageDateTime as Date).getTime() -
						new Date(a.lastMessageDateTime as Date).getTime()
					);
				});
				setArchivedConversations(sortedArchivedConversations);
			}

			// Always update the next token from the API response
			setArchivedConversationsNextToken(recentArchivedConversations?.next || null);

			// Reset loading state if needed
			if (isLoadingMore) {
				setIsLoadingMore(false);
			}
		}
	}, [recentArchivedConversations, recentArchivedConversationsRS]);

	useEffect(() => {
		if (currentConversation && singleConversationRS.status === RequestStatus.SUCCESS) {
			const sortedMessages = [...(singleConversation?.items || [])].sort((a: Conversation, b: Conversation) => {
				const timeA = a.lastMessageDateTime ? new Date(a.lastMessageDateTime).getTime() : 0;
				const timeB = b.lastMessageDateTime ? new Date(b.lastMessageDateTime).getTime() : 0;
				return timeA - timeB;
			});

			const hasGreeting = sortedMessages.length > 0 && sortedMessages[0].sender === 'assistant';

			if (!hasGreeting && sortedMessages.length > 0) {
				const greetingMessage: Conversation = {
					id: Date.now().toString() + '-greeting',
					text: t(
						'pages.aiAssistant.greeting',
						"Hi there! I'm Ana, your personal assistant. How can I help you today?"
					),
					sender: 'assistant',
					creationDateTime: new Date(
						(sortedMessages[0].creationDateTime
							? new Date(sortedMessages[0].creationDateTime).getTime()
							: Date.now()) - 1000
					)
				};
				sortedMessages.unshift(greetingMessage);
			}

			setMessages(sortedMessages);

			setTimeout(scrollToBottom, 100);
		}
	}, [currentConversation, singleConversation, singleConversationRS, t]);

	useEffect(() => {
		if (currentConversation === null) {
			setMessages([]);
		}
	}, [currentConversation]);

	const handleFeedback = (messageId: string, type: 'positive' | 'negative' | null, comment?: string) => {
		const currentMessage = messages.find((msg) => msg.id === messageId);
		const currentFeedback = currentMessage?.feedback || messageFeedback[messageId];

		const newFeedback = currentFeedback ? (type === currentFeedback ? currentFeedback : type) : type;

		if (newFeedback) {
			// Store feedback in a separate state object to avoid triggering message array changes
			setMessageFeedback((prev) => ({
				...prev,
				[messageId]: newFeedback
			}));

			messageId &&
				newFeedback &&
				dispatch(
					sendFeedback({
						userId: user?.id || '',
						messageId,
						type: newFeedback,
						comment: newFeedback === 'negative' ? comment : undefined
					})
				);
		}

		setPressedMessageId(null);
	};

	const handleTouchStart = (messageId: string) => {
		if (isMobile) {
			const timer = setTimeout(() => {
				setPressedMessageId(messageId);
				// Apply haptic feedback if available
				if (navigator.vibrate) {
					navigator.vibrate(50);
				}
			}, 500);

			setLongPressTimer(timer);
		}
	};

	const handleTouchEnd = () => {
		if (longPressTimer) {
			clearTimeout(longPressTimer);
			setLongPressTimer(null);
		}
	};

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		// Only scroll to bottom when new messages are added, not when feedback is updated
		const hasNewMessage = messages.some((msg) => !msg.feedback || msg.isTyping);
		if (hasNewMessage) {
			scrollToBottom();
		}
	}, [messages]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInput(e.target.value);
	};

	const createNewConversation = (initialMessage: string) => {
		const tempConversationId = `temp-${Date.now().toString()}`;
		const title = initialMessage.length > 20 ? initialMessage.substring(0, 20) + '...' : initialMessage;

		const newConversation = {
			id: tempConversationId,
			title: title,
			creationDateTime: new Date()
		};

		dispatch(resetAIAssistant());

		// Add the new temporary conversation to the beginning of the list
		// to maintain correct order while we wait for the real ID
		setConversations([newConversation]);
		setCurrentConversation(tempConversationId);

		const greetingMessage: Conversation = {
			id: Date.now().toString() + '-greeting',
			text: t(
				'pages.aiAssistant.greeting',
				"Hi there! I'm Ana, your personal assistant. How can I help you today?"
			),
			sender: 'assistant',
			creationDateTime: new Date()
		};

		const userMessage: Conversation = {
			id: Date.now().toString(),
			text: initialMessage,
			sender: 'user',
			creationDateTime: new Date(new Date().getTime() + 1000) // Ensure user message appears after greeting
		};

		setMessages([greetingMessage, userMessage]);
		setInput('');
		setIsTyping(true);
		setIsGenerating(true);
	};

	const handleSendMessage = ({ starterMessage }: { starterMessage: string }) => {
		if (!starterMessage && (input.trim() === '' || isGenerating)) return;

		if (currentConversation === null) {
			createNewConversation(starterMessage);
		} else {
			const userMessage: Conversation = {
				id: Date.now().toString(),
				text: input || starterMessage,
				sender: 'user',
				creationDateTime: new Date()
			};

			setMessages([...messages, userMessage]);
			setInput('');
			setIsTyping(true);
			setIsGenerating(true);
		}

		dispatch(
			createConversation({
				userId: user?.id || '',
				query: input || starterMessage,
				agentType: AgentType.CHAT_AGENT,
				...(currentConversation && { conversationId: currentConversation })
			})
		)
			.then((res) => {
				const responseData = res.payload as any;

				const responseText = responseData.response.answerText || responseData.response || responseData;
				const responseTitle = responseData.title || '';
				const newConversationId = responseData.conversationId;
				const kbMessageId = responseData.response.kbMessageId || responseData.kbMessageId;
				const webCitations = responseData.response.webCitations || responseData.webCitations;

				if (currentConversation === null && responseTitle && newConversationId) {
					setConversations(
						conversations.map((conv) =>
							conv.id === conversations[0].id
								? { ...conv, id: newConversationId, title: responseTitle }
								: conv
						)
					);
					setCurrentConversation(newConversationId);

					// Update Redux store with the new conversation at the beginning of the list
					// instead of overwriting existing conversations
					const newConversationObj = {
						id: newConversationId,
						title: responseTitle,
						creationDateTime: new Date()
					};

					// Make sure we don't have duplicates and sort by creation date (newest first)
					const filteredConversations = conversations.filter(
						(c) => c.id !== newConversationId && c.id !== conversations[0].id
					);

					// Put the new conversation at the beginning (it's the newest)
					dispatch(
						updateRecentConversation({
							payload: [newConversationObj, ...filteredConversations],
							agentType: AgentType.CHAT_AGENT
						})
					);
				}

				const aiMessage: Conversation = {
					id: kbMessageId,
					text: responseText,
					sender: 'assistant',
					creationDateTime: new Date(),
					isTyping: false,
					...(webCitations && { webCitations })
				};

				setMessages((prev) => [...prev, aiMessage]);
				setIsTyping(false);
				setIsGenerating(false);
				// Save the ID of the latest AI message

				// Find and scroll to the latest AI response
				setTimeout(() => {
					const latestAiMessage = document.getElementById(`message-${aiMessage.id}`);
					if (latestAiMessage) {
						latestAiMessage.scrollIntoView({ behavior: 'smooth' });
					} else {
						scrollToBottom();
					}
				}, 100);
			})
			.catch(() => {
				const errorMessage: Conversation = {
					id: Date.now().toString(),
					text: 'Sorry, something went wrong. Please try again later.',
					sender: 'assistant',
					creationDateTime: new Date(),
					isTyping: false
				};
				setMessages((prev) => [...prev, errorMessage]);
				setIsTyping(false);
				setIsGenerating(false);
				scrollToBottom();
			});
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey && !isGenerating) {
			e.preventDefault();
			handleSendMessage({ starterMessage: input });
		}
	};

	const formatTime = (date: string | Date): string => {
		const now = new Date();
		const diffInDays = Math.floor((now.getTime() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));

		if (diffInDays === 0) {
			const minutes = new Date(date).getMinutes();
			return `${new Date(date).getHours()}:${minutes < 10 ? '0' + minutes : minutes}`;
		} else if (diffInDays < 7) {
			return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
		} else if (diffInDays < 30) {
			const weeks = Math.floor(diffInDays / 7);
			return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
		} else {
			const months = Math.floor(diffInDays / 30);
			return `${months} month${months > 1 ? 's' : ''} ago`;
		}
	};

	const handleSelectConversation = (conversationId: string) => {
		setIsTransitioning(true);
		setShowConversationList(false);

		setTimeout(() => {
			setCurrentConversation(conversationId);

			// Fetch the complete conversation including user messages and AI responses
			dispatch(readSingleConversation({ userId: user?.id || '', conversationId })).then(() => {
				// Make sure we scroll to the bottom when conversation loads
				setTimeout(scrollToBottom, 100);
			});

			setShowChatInterface(true);
			setIsTransitioning(false);
		}, 300);
	};

	const goBackToConversationList = () => {
		setIsTransitioning(true);
		setShowChatInterface(false);

		setTimeout(() => {
			// Refresh the conversation lists to make sure we have the latest data
			if (user) {
				if (showArchivedConversations) {
					dispatch(
						readArchivedConversation({
							userId: user.id,
							limit: 20,
							next: null,
							agentType: AgentType.CHAT_AGENT
						})
					);
				} else {
					dispatch(
						readConversation({ userId: user.id, limit: 20, next: null, agentType: AgentType.CHAT_AGENT })
					);
				}
			}

			setCurrentConversation(null);
			setShowConversationList(true);
			setIsTransitioning(false);
		}, 300);
	};

	useEffect(() => {
		if (currentConversation === null && !isTransitioning) {
			setShowConversationList(true);
			setShowChatInterface(false);
		} else if (currentConversation !== null && !isTransitioning) {
			setShowConversationList(false);
			setShowChatInterface(true);
		}
	}, [currentConversation, isTransitioning]);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, conversationId: string) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setSelectedConversationId(conversationId);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
	};

	const handleArchiveConversation = () => {
		if (selectedConversationId) {
			setConfirmDialogAction('archive');
			setConfirmDialogOpen(true);
			handleMenuClose();
		}
	};

	const handleDeleteConversation = () => {
		if (selectedConversationId) {
			setConfirmDialogAction('delete');
			setConfirmDialogOpen(true);
			handleMenuClose();
		}
	};

	const handleConfirmDialogClose = (confirm: boolean) => {
		if (confirm && selectedConversationId && confirmDialogAction) {
			if (confirmDialogAction === 'delete') {
				// Filter out the deleted conversation from local state
				const updatedConversations = conversations.filter((c) => c.id !== selectedConversationId);
				const updatedArchivedConversations = archivedConversations.filter(
					(c) => c.id !== selectedConversationId
				);

				setConversations(updatedConversations);
				setArchivedConversations(updatedArchivedConversations);

				if (currentConversation === selectedConversationId) {
					setCurrentConversation(null);
					setMessages([]);
				}

				// Update Redux store with filtered data
				dispatch(deleteConversation({ userId: user?.id || '', conversationId: selectedConversationId }));
				dispatch(
					updateRecentConversation({
						payload: updatedConversations,
						agentType: AgentType.CHAT_AGENT
					})
				);
				dispatch(updateRecentArchivedConversation(updatedArchivedConversations));
			} else if (confirmDialogAction === 'archive') {
				const conversation = showArchivedConversations
					? archivedConversations.find((c) => c.id === selectedConversationId)
					: conversations.find((c) => c.id === selectedConversationId);

				if (conversation) {
					if (showArchivedConversations) {
						const updatedArchivedConversations = archivedConversations.filter(
							(c) => c.id !== selectedConversationId
						);
						const updatedActiveConversations = [
							...conversations,
							{ ...conversation, archived: false }
						].sort((a, b) => {
							return (
								new Date(b.lastMessageDateTime as Date).getTime() -
								new Date(a.lastMessageDateTime as Date).getTime()
							);
						});

						setArchivedConversations(updatedArchivedConversations);
						setConversations(updatedActiveConversations);

						dispatch(updateRecentArchivedConversation(updatedArchivedConversations));
						dispatch(
							updateRecentConversation({
								payload: updatedActiveConversations,
								agentType: AgentType.CHAT_AGENT
							})
						);
					} else {
						const updatedActiveConversations = conversations.filter((c) => c.id !== selectedConversationId);
						const updatedArchivedConversations = [
							...archivedConversations,
							{ ...conversation, archived: true }
						].sort((a, b) => {
							return (
								new Date(b.lastMessageDateTime as Date).getTime() -
								new Date(a.lastMessageDateTime as Date).getTime()
							);
						});

						setConversations(updatedActiveConversations);
						setArchivedConversations(updatedArchivedConversations);

						dispatch(
							updateRecentConversation({
								payload: updatedActiveConversations,
								agentType: AgentType.CHAT_AGENT
							})
						);
						dispatch(updateRecentArchivedConversation(updatedArchivedConversations));
					}

					if (currentConversation === selectedConversationId) {
						setCurrentConversation(null);
						setMessages([]);
					}

					dispatch(
						archiveConversation({
							userId: user?.id || '',
							conversationId: selectedConversationId,
							archive: !showArchivedConversations
						})
					);
				}
			}
		}
		setConfirmDialogOpen(false);
		setConfirmDialogAction(null);
		setSelectedConversationId(null);
	};

	const toggleArchivedConversations = () => {
		// Switch between active and archived conversations
		setShowArchivedConversations(!showArchivedConversations);
	};

	// Add a function to load more conversations
	const handleLoadMoreConversations = () => {
		setIsLoadingMore(true);

		if (showArchivedConversations) {
			dispatch(
				readArchivedConversation({
					userId: user?.id || '',
					limit: 20,
					next: Buffer.from(JSON.stringify(archivedConversationsNextToken), 'utf-8').toString('base64'),
					agentType: AgentType.CHAT_AGENT
				})
			)
				.then((res) => {
					const responseData = res.payload as any;

					// If there's no new data, exit early
					if (!responseData?.Items?.length) {
						setIsLoadingMore(false);
						return;
					}

					const newItems = responseData.Items || [];
					const nextToken = responseData.next || null;

					// Create combined list, filtering out duplicates by id
					const existingIds = new Set(archivedConversations.map((c) => c.id));
					const filteredNewItems = newItems.filter((item: RecentConversation) => !existingIds.has(item.id));

					// Combine lists and sort by creation date (newest first)
					const combinedArchivedConversations = [...archivedConversations, ...filteredNewItems].sort(
						(a, b) => {
							return (
								new Date(b.lastMessageDateTime).getTime() - new Date(a.lastMessageDateTime).getTime()
							);
						}
					);

					// Update local state
					setArchivedConversations(combinedArchivedConversations);
					setArchivedConversationsNextToken(nextToken);

					// Update Redux store to match our local state
					dispatch(updateRecentArchivedConversation(combinedArchivedConversations));

					setIsLoadingMore(false);
				})
				.catch(() => {
					console.error('Error loading more archived conversations');
					setIsLoadingMore(false);
					// Show error notification
					enqueueSnackbar('Failed to load more conversations', { variant: 'error' });
				});
		} else {
			dispatch(
				readConversation({
					userId: user?.id || '',
					limit: 20,
					next: Buffer.from(JSON.stringify(activeConversationsNextToken), 'utf-8').toString('base64'),
					agentType: AgentType.CHAT_AGENT
				})
			)
				.then((res) => {
					const responseData = res.payload as any;

					// If there's no new data, exit early
					if (!responseData?.Items?.length) {
						setIsLoadingMore(false);
						return;
					}

					const newItems = responseData.Items || [];
					const nextToken = responseData.next || null;

					// Create combined list, filtering out duplicates by id
					const existingIds = new Set(conversations.map((c) => c.id));
					const filteredNewItems = newItems.filter((item: RecentConversation) => !existingIds.has(item.id));

					// Combine lists and sort by creation date (newest first)
					const combinedConversations = [...conversations, ...filteredNewItems].sort((a, b) => {
						return new Date(b.lastMessageDateTime).getTime() - new Date(a.lastMessageDateTime).getTime();
					});

					// Update local state
					setConversations(combinedConversations);
					setActiveConversationsNextToken(nextToken);

					// Update Redux store to match our local state
					dispatch(
						updateRecentConversation({
							payload: combinedConversations,
							agentType: AgentType.CHAT_AGENT
						})
					);

					setIsLoadingMore(false);
				})
				.catch(() => {
					console.error('Error loading more conversations');
					setIsLoadingMore(false);
					// Show error notification
					enqueueSnackbar('Failed to load more conversations', { variant: 'error' });
				});
		}
	};

	const renderConversationsList = () => {
		const filteredConversations = showArchivedConversations ? archivedConversations : conversations;
		const hasNextToken = showArchivedConversations ? archivedConversationsNextToken : activeConversationsNextToken;
		const isInitialLoading = showArchivedConversations
			? (recentArchivedConversationsRS.status === RequestStatus.PENDING ||
					recentArchivedConversationsRS.status === RequestStatus.IDLE) &&
			  archivedConversations.length === 0
			: (recentConversationsRS.status === RequestStatus.PENDING ||
					recentConversationsRS.status === RequestStatus.IDLE) &&
			  conversations.length === 0;

		return (
			<Fade in={showConversationList} timeout={400}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						position: 'absolute',
						width: '100%'
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: { xs: 'column', md: 'row' },
							alignItems: { xs: 'center', md: 'center' },
							justifyContent: 'space-between',
							position: 'sticky',
							zIndex: 1000,
							p: 2,
							gap: { xs: 0.5, md: 0 }
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<HistoryIcon sx={{ color: 'primary.main', mr: 1 }} />
							<Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
								{showArchivedConversations
									? t('pages.aiAssistant.archivedConversations')
									: t('pages.aiAssistant.activeConversations')}
							</Typography>
						</Box>
						<Button
							size="small"
							startIcon={<ArchiveOutlinedIcon />}
							onClick={toggleArchivedConversations}
							sx={{ textTransform: 'none' }}
						>
							{showArchivedConversations
								? t('pages.aiAssistant.showActiveConversations')
								: t('pages.aiAssistant.showArchivedConversations')}
						</Button>
					</Box>
					<Box
						sx={{
							px: 2,
							pb: 2,
							flex: 1,
							overflowY: 'auto',
							'&::-webkit-scrollbar': {
								display: 'none'
							},
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							maxHeight: { xs: 'calc(100% - 250px)', sm: 'calc(100% - 230px)' }
						}}
					>
						{isInitialLoading ? (
							<List sx={{ width: '100%' }}>
								{[...Array(5)].map((_, i) => (
									<ListItem key={i} sx={{ borderRadius: 2, mb: 1, border: '1px solid transparent' }}>
										<ListItemAvatar>
											<Skeleton variant="circular" width={40} height={40} />
										</ListItemAvatar>
										<ListItemText
											primary={<Skeleton variant="text" width="60%" />}
											secondary={<Skeleton variant="text" width="30%" />}
										/>
									</ListItem>
								))}
							</List>
						) : filteredConversations?.length === 0 ? (
							<Box sx={{ textAlign: 'center', py: 4 }}>
								<Typography color="text.secondary">
									{showArchivedConversations
										? 'No archived conversations'
										: 'No recent conversations'}
								</Typography>
							</Box>
						) : (
							<>
								<List sx={{ width: '100%' }}>
									{filteredConversations?.map((conversation) => (
										<React.Fragment key={conversation.id}>
											<ListItem
												onClick={() => handleSelectConversation(conversation.id)}
												sx={{
													borderRadius: 2,
													mb: 1,
													transition: 'all 0.2s ease',
													'&:hover': {
														backgroundColor: 'rgba(33, 150, 243, 0.08)',
														transform: 'translateX(5px)',
														cursor: 'pointer'
													},
													border: (theme: any) =>
														`1px solid ${
															theme.palette.mode === 'dark'
																? 'rgba(255, 255, 255, 0.08)'
																: 'rgba(0, 0, 0, 0.08)'
														}`,
													position: 'relative'
												}}
											>
												<ListItemAvatar>
													<PulsingAvatar
														sx={{
															width: 40,
															height: 40,
															background:
																'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
															color: 'white'
														}}
													>
														<SmartToyOutlinedIcon />
													</PulsingAvatar>
												</ListItemAvatar>
												<ListItemText
													primary={conversation.title}
													secondary={formatTime(conversation.lastMessageDateTime as Date)}
													primaryTypographyProps={{
														fontWeight: 500,
														color: 'text.primary',
														textTransform: 'capitalize',
														fontSize: '0.95rem'
													}}
													secondaryTypographyProps={{
														fontSize: '0.8rem',
														color: 'text.secondary'
													}}
												/>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<IconButton
														size="small"
														onClick={(e) => handleMenuOpen(e, conversation.id)}
														sx={{ ml: 1 }}
													>
														<MoreVertIcon fontSize="small" />
													</IconButton>
												</Box>
											</ListItem>
										</React.Fragment>
									))}
								</List>

								{/* Show More Button */}
								{hasNextToken && (
									<Box sx={{ textAlign: 'center', my: 2 }}>
										<Button
											onClick={handleLoadMoreConversations}
											disabled={isLoadingMore}
											sx={{
												textTransform: 'none',
												fontWeight: 500,
												color: 'primary.main',
												border: 'none',
												boxShadow: 'none',
												backgroundColor: 'transparent',
												'&:hover': {
													backgroundColor: 'rgba(33, 150, 243, 0.08)',
													border: 'none',
													boxShadow: 'none'
												}
											}}
										>
											{isLoadingMore ? (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<Box
														sx={{
															width: 16,
															height: 16,
															borderRadius: '50%',
															border: '2px solid rgba(33, 150, 243, 0.3)',
															borderTopColor: 'primary.main',
															animation: 'spin 1s linear infinite',
															'@keyframes spin': {
																'0%': {
																	transform: 'rotate(0deg)'
																},
																'100%': {
																	transform: 'rotate(360deg)'
																}
															}
														}}
													/>
													<Typography variant="body2">Loading more...</Typography>
												</Box>
											) : (
												'Show more'
											)}
										</Button>
									</Box>
								)}
							</>
						)}
					</Box>
					{/* Suggested topics and input */}
					<Box
						sx={{
							position: 'fixed',
							bottom: 0,
							left: { xs: 0, sm: '50%' },
							transform: { xs: 'none', sm: 'translateX(-50%)' },
							width: '100%',
							pt: 2,
							backgroundColor: (theme) =>
								theme.palette.mode === 'dark' ? 'rgba(0, 15, 35, 0.95)' : 'rgba(255, 255, 255, 0.95)',
							borderTop: (theme) =>
								theme.palette.mode === 'dark'
									? '1px solid rgba(255, 255, 255, 0.08)'
									: '1px solid rgba(0, 0, 0, 0.05)',
							boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.05)',
							zIndex: 1000
						}}
					>
						<Box sx={{ mb: 1, px: isXs ? 1 : 2 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: isXs ? 0.5 : 1 }}>
								<LightbulbOutlinedIcon
									sx={{
										color: 'primary.main',
										mr: 1,
										fontSize: isXs ? '0.8rem' : isMobile ? '0.9rem' : '1rem'
									}}
								/>
								<Typography
									variant="subtitle2"
									color="text.secondary"
									sx={{ fontSize: isXs ? '0.75rem' : isMobile ? '0.8rem' : 'inherit' }}
								>
									{t('pages.aiAssistant.suggestedTopics')}
								</Typography>
							</Box>
							<Box
								sx={{
									display: 'flex',
									justifyContent: { xs: 'center', sm: 'center' },
									gap: isXs ? 0.25 : isMobile ? 0.5 : 1,
									flexWrap: 'wrap',
									pt: isXs ? 0.5 : 1
								}}
							>
								<Button
									variant="outlined"
									sx={{
										borderRadius: 4,
										textTransform: 'capitalize',
										fontSize: isXs ? 10 : isMobile ? 12 : 14,
										px: isXs ? 1 : isMobile ? 1.5 : 2,
										py: isXs ? 0.25 : 0.5,
										borderColor: 'primary.light',
										color: 'primary.main',
										'&:hover': {
											backgroundColor: 'rgba(33, 150, 243, 0.08)',
											borderColor: 'primary.main'
										}
									}}
									onClick={() =>
										handleSendMessage({ starterMessage: t('pages.aiAssistant.myWallet.question') })
									}
								>
									{t('pages.aiAssistant.myWallet.title')}
								</Button>
								<Button
									variant="outlined"
									sx={{
										borderRadius: 4,
										textTransform: 'capitalize',
										fontSize: isXs ? 10 : isMobile ? 12 : 14,
										px: isXs ? 1 : isMobile ? 1.5 : 2,
										py: isXs ? 0.25 : 0.5,
										borderColor: 'primary.light',
										color: 'primary.main',
										'&:hover': {
											backgroundColor: 'rgba(33, 150, 243, 0.08)',
											borderColor: 'primary.main'
										}
									}}
									onClick={() =>
										handleSendMessage({
											starterMessage: t('pages.aiAssistant.myVerification.question')
										})
									}
								>
									{t('pages.aiAssistant.myVerification.title')}
								</Button>
								<Button
									variant="outlined"
									sx={{
										borderRadius: 4,
										textTransform: 'capitalize',
										fontSize: isXs ? 10 : isMobile ? 12 : 14,
										px: isXs ? 1 : isMobile ? 1.5 : 2,
										py: isXs ? 0.25 : 0.5,
										borderColor: 'primary.light',
										color: 'primary.main',
										'&:hover': {
											backgroundColor: 'rgba(33, 150, 243, 0.08)',
											borderColor: 'primary.main'
										}
									}}
									onClick={() =>
										handleSendMessage({ starterMessage: t('pages.aiAssistant.myJourney.question') })
									}
								>
									{t('pages.aiAssistant.myJourney.title')}
								</Button>
							</Box>
						</Box>

						<AIAssistantInput
							input={input}
							handleInputChange={handleInputChange}
							handleKeyPress={handleKeyPress}
							handleSendMessage={() => handleSendMessage({ starterMessage: input })}
							disabled={isGenerating}
							isMobile={isMobile}
							isXs={isXs}
						/>
					</Box>
				</Box>
			</Fade>
		);
	};

	const renderChatInterface = () => {
		const selectedConversation = showArchivedConversations
			? archivedConversations?.find((c) => c.id === currentConversation)
			: conversations?.find((c) => c.id === currentConversation);

		return (
			<Fade in={showChatInterface} timeout={400}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						position: 'absolute',
						width: '100%'
					}}
				>
					<Box
						sx={{
							p: isXs ? 1 : isMobile ? 1.5 : 2,
							display: 'flex',
							alignItems: 'center',
							borderBottom: (theme) =>
								theme.palette.mode === 'dark'
									? '1px solid rgba(255, 255, 255, 0.08)'
									: '1px solid rgba(0, 0, 0, 0.05)',
							backgroundColor: (theme) =>
								theme.palette.mode === 'dark' ? 'rgba(0, 30, 69, 0.7)' : 'rgba(255, 255, 255, 0.7)'
						}}
					>
						<Tooltip title="Back to conversations">
							<IconButton
								edge="start"
								color="primary"
								onClick={goBackToConversationList}
								sx={{ mr: isXs ? 0.5 : 1 }}
								disabled={isGenerating}
							>
								<ChevronRightIcon
									sx={{ transform: 'rotate(180deg)', fontSize: isXs ? '1.2rem' : 'inherit' }}
								/>
							</IconButton>
						</Tooltip>
						<PulsingAvatar
							sx={{
								background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
								color: 'white',
								width: isXs ? 32 : isMobile ? 36 : 40,
								height: isXs ? 32 : isMobile ? 36 : 40
							}}
						>
							<SmartToyOutlinedIcon sx={{ fontSize: isXs ? '1.2rem' : 'inherit' }} />
						</PulsingAvatar>
						<Box pl={isXs ? 0.5 : isMobile ? 1 : 1.5}>
							<Typography
								variant="subtitle1"
								color="text.primary"
								fontWeight={600}
								textTransform="capitalize"
								sx={{ fontSize: isXs ? '0.85rem' : isMobile ? '0.9rem' : '1rem' }}
							>
								{selectedConversation ? selectedConversation.title : 'New Conversation'}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ fontSize: isXs ? '0.7rem' : 'inherit' }}
							>
								{formatTime(
									(selectedConversation?.lastMessageDateTime as Date) ||
										selectedConversation?.creationDateTime ||
										new Date()
								) ?? `${new Date().getHours()}:${new Date().getMinutes()}`}
							</Typography>
						</Box>
					</Box>

					<Box
						sx={{
							flexGrow: 1,
							overflow: 'auto',
							p: isXs ? 1 : isMobile ? 1.5 : 2,
							display: 'flex',
							flexDirection: 'column',
							gap: isXs ? 1.5 : 2,
							backgroundColor: (theme: any) =>
								theme.palette.mode === 'dark' ? 'rgba(0, 15, 35, 0.3)' : 'rgba(240, 245, 255, 0.3)'
						}}
					>
						{singleConversationRS.status === RequestStatus.PENDING && messages.length === 0 && (
							<>
								{/* Skeleton for loading conversation messages */}
								<Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
									<Skeleton variant="circular" width={40} height={40} />
									<Skeleton
										variant="rounded"
										width="55%"
										height={60}
										sx={{ borderRadius: '20px 20px 20px 0' }}
									/>
								</Box>
								<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
									<Skeleton
										variant="rounded"
										width="45%"
										height={40}
										sx={{ borderRadius: '20px 20px 0 20px' }}
									/>
								</Box>
								<Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
									<Skeleton variant="circular" width={40} height={40} />
									<Skeleton
										variant="rounded"
										width="65%"
										height={80}
										sx={{ borderRadius: '20px 20px 20px 0' }}
									/>
								</Box>
								<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
									<Skeleton
										variant="rounded"
										width="40%"
										height={40}
										sx={{ borderRadius: '20px 20px 0 20px' }}
									/>
								</Box>
							</>
						)}
						{messages.map((message) => {
							// Use feedback from separate state if available
							const feedbackValue = message.id
								? messageFeedback[message.id] || message.feedback
								: message.feedback;

							return (
								<Box
									key={message.id}
									id={`message-${message.id}`}
									className={message.sender === 'assistant' ? 'message-container' : ''}
									sx={{
										display: 'flex',
										justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
										mb: 1,
										gap: 1,
										opacity: 1,
										position: 'relative',
										padding: pressedMessageId === message.id && isMobile ? 2 : 0,
										margin: pressedMessageId === message.id && isMobile ? -2 : 0,
										borderRadius: pressedMessageId === message.id && isMobile ? 2 : 0,
										backgroundColor:
											pressedMessageId === message.id && isMobile
												? 'rgba(0, 0, 0, 0.03)'
												: 'transparent',
										transition: 'all 0.2s ease-in-out',
										zIndex: pressedMessageId === message.id ? 2 : 1
									}}
									onTouchStart={
										message.sender === 'assistant' && !message.isTyping
											? () => handleTouchStart(message?.id || '')
											: undefined
									}
									onTouchEnd={handleTouchEnd}
									onTouchMove={handleTouchEnd}
									onTouchCancel={handleTouchEnd}
								>
									{message.sender === 'assistant' && (
										<PulsingAvatar
											sx={{
												mr: isXs ? 0.5 : 1,
												width: isXs ? 32 : isMobile ? 36 : 40,
												height: isXs ? 32 : isMobile ? 36 : 40,
												background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
												color: 'white'
											}}
										>
											<SmartToyOutlinedIcon sx={{ fontSize: isXs ? '1rem' : 'inherit' }} />
										</PulsingAvatar>
									)}
									<Paper
										elevation={0}
										className="chat-bubble"
										sx={(theme) => ({
											p: isXs ? 1 : isMobile ? 1.5 : 2,
											maxWidth: isXs ? '85%' : isMobile ? '80%' : '70%',
											borderRadius:
												message.sender === 'user' ? '20px 20px 0 20px' : '20px 20px 20px 0',
											backgroundColor:
												message.sender === 'user'
													? theme.palette.primary.main
													: theme.palette.mode === 'dark'
													? 'rgba(255,255,255,0.08)'
													: '#f0f2f5',
											border:
												message.sender !== 'user'
													? theme.palette.mode === 'dark'
														? '1px solid rgba(255,255,255,0.15)'
														: '1px solid rgba(0,0,0,0.06)'
													: 'none',
											color:
												message.sender === 'user'
													? theme.palette.primary.contrastText
													: theme.palette.text.primary,
											boxShadow:
												pressedMessageId === message.id && isMobile
													? '0 4px 16px rgba(0, 0, 0, 0.15)'
													: message.sender === 'user'
													? '0 2px 10px rgba(33, 150, 243, 0.2)'
													: '0 2px 10px rgba(0, 0, 0, 0.05)',
											position: 'relative',
											transition: 'box-shadow 0.2s ease-in-out',
											transform:
												pressedMessageId === message.id && isMobile
													? 'scale(1.02)'
													: 'scale(1)',
											'& pre': {
												margin: '8px 0',
												borderRadius: '6px',
												overflow: 'auto',
												maxWidth: isMobile ? '100%' : 'auto',
												fontSize: isXs ? '0.75rem' : isMobile ? '0.8rem' : '0.9rem'
											},
											'& code': {
												fontFamily: 'monospace',
												fontSize: isXs ? '0.75rem' : isMobile ? '0.8rem' : '0.9em',
												padding: message.sender === 'user' ? '2px 4px' : '2px 4px',
												borderRadius: '4px',
												backgroundColor:
													message.sender === 'user'
														? 'rgba(255, 255, 255, 0.2)'
														: 'rgba(0, 0, 0, 0.05)'
											},
											'& p': {
												margin: isXs ? '4px 0' : '8px 0',
												fontSize: isXs ? '0.8rem' : isMobile ? '0.9rem' : 'inherit'
											},
											'& ul, & ol': {
												paddingLeft: isXs ? '12px' : isMobile ? '16px' : '20px',
												margin: isXs ? '4px 0' : '8px 0'
											},
											'& blockquote': {
												borderLeft: '4px solid',
												borderColor:
													message.sender === 'user'
														? 'rgba(255, 255, 255, 0.3)'
														: 'rgba(33, 150, 243, 0.3)',
												paddingLeft: isXs ? '8px' : '12px',
												margin: isXs ? '4px 0' : '8px 0',
												fontStyle: 'italic'
											},
											'& a': {
												color:
													message.sender === 'user'
														? 'rgba(255, 255, 255, 0.9)'
														: 'primary.main',
												textDecoration: 'underline'
											},
											'& table': {
												borderCollapse: 'collapse',
												width: '100%',
												margin: isXs ? '8px 0' : '12px 0',
												fontSize: isXs ? '0.75rem' : 'inherit'
											},
											'& th, & td': {
												border: '1px solid',
												borderColor:
													message.sender === 'user'
														? 'rgba(255, 255, 255, 0.2)'
														: 'rgba(0, 0, 0, 0.1)',
												padding: isXs ? '4px 6px' : '6px 8px',
												textAlign: 'left'
											},
											'& th': {
												backgroundColor:
													message.sender === 'user'
														? 'rgba(255, 255, 255, 0.1)'
														: 'rgba(0, 0, 0, 0.05)'
											}
										})}
									>
										{message.sender === 'assistant' && message.isTyping ? (
											<Typography
												variant="body1"
												sx={{
													lineHeight: 1.6,
													'& .typing-cursor': {
														animation: 'blink 1s step-end infinite'
													},
													'@keyframes blink': {
														'from, to': { opacity: 1 },
														'50%': { opacity: 0 }
													}
												}}
											>
												<TypingText
													text={message.text}
													speed={20}
													onComplete={() => {
														setMessages((prev) =>
															prev.map((msg) =>
																msg.id === message.id
																	? { ...msg, isTyping: false }
																	: msg
															)
														);

														setIsGenerating(false);
													}}
												/>
											</Typography>
										) : message.sender === 'assistant' ? (
											<>
												<ReactMarkdown
													components={{
														code: CodeBlock
													}}
												>
													{message.text}
												</ReactMarkdown>
												{message.webCitations && message.webCitations.length > 0 && (
													<Box
														sx={{
															mt: 1.5,
															pt: 1.5,
															borderTop: '1px solid',
															borderColor: 'divider'
														}}
													>
														<Box
															sx={{
																display: 'flex',
																alignItems: 'center',
																gap: 0.5,
																mb: 1
															}}
														>
															<LanguageIcon
																sx={{ fontSize: 14, color: 'text.secondary' }}
															/>
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ fontWeight: 600, letterSpacing: '0.02em' }}
															>
																Web Sources
															</Typography>
														</Box>
														<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
															{message.webCitations.slice(0, 3).map((citation, idx) => (
																<Box
																	key={idx}
																	component="a"
																	href={citation.url}
																	target="_blank"
																	rel="noopener noreferrer"
																	sx={{
																		display: 'inline-flex',
																		alignItems: 'center',
																		gap: 0.5,
																		px: 1.25,
																		py: 0.5,
																		borderRadius: '16px',
																		border: '1px solid',
																		borderColor: 'divider',
																		color: 'primary.main',
																		textDecoration: 'none',
																		fontSize: '0.75rem',
																		lineHeight: 1.4,
																		transition: 'all 0.15s ease',
																		maxWidth: '100%',
																		'&:hover': {
																			backgroundColor: 'action.hover',
																			borderColor: 'primary.main'
																		}
																	}}
																>
																	<LanguageIcon sx={{ fontSize: 12, opacity: 0.7 }} />
																	<Box
																		component="span"
																		sx={{
																			overflow: 'hidden',
																			textOverflow: 'ellipsis',
																			whiteSpace: 'nowrap'
																		}}
																	>
																		{citation.domain || citation.url}
																	</Box>
																</Box>
															))}
															{message.webCitations.length > 3 && (
																<Box
																	sx={{
																		display: 'inline-flex',
																		alignItems: 'center',
																		px: 1.25,
																		py: 0.5,
																		borderRadius: '16px',
																		fontSize: '0.75rem',
																		lineHeight: 1.4,
																		color: 'text.secondary'
																	}}
																>
																	+{message.webCitations.length - 3} more
																</Box>
															)}
														</Box>
													</Box>
												)}
											</>
										) : (
											<Typography variant="body1" sx={{ lineHeight: 1.6 }}>
												{message.text}
											</Typography>
										)}

										{/* Feedback indicator if feedback was given */}
										{message.sender === 'assistant' && feedbackValue && !message.isTyping && (
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'flex-end',
													mt: 1,
													opacity: 0.7
												}}
											>
												<Typography
													variant="caption"
													sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
												>
													{feedbackValue === 'positive' ? (
														<>
															<ThumbUpIcon fontSize="inherit" color="success" /> Helpful
														</>
													) : (
														<>
															<ThumbDownIcon fontSize="inherit" color="error" /> Not
															helpful
														</>
													)}
												</Typography>
											</Box>
										)}
									</Paper>

									{/* Feedback buttons for assistant messages */}
									{message.sender === 'assistant' &&
										!message.isTyping &&
										message.id &&
										!message.id.includes('-greeting') && (
											<>
												{/* // Desktop */}
												{!isMobile && (
													<MessageFeedback
														messageId={message?.id || ''}
														feedback={feedbackValue || null}
														onFeedback={handleFeedback}
														isMobile={false}
													/>
												)}

												{/* // Mobile */}
												{isMobile && pressedMessageId === message.id && (
													<Zoom in={true}>
														<Box
															onClick={(e: React.MouseEvent) => e.stopPropagation()}
															onTouchEnd={(e: React.TouchEvent) => e.stopPropagation()}
															sx={{
																position: 'absolute',
																width: '100%',
																display: 'flex',
																justifyContent: 'center',
																zIndex: 5
															}}
														>
															<MessageFeedback
																messageId={message.id || ''}
																feedback={feedbackValue || null}
																onFeedback={handleFeedback}
																isMobile={true}
															/>
														</Box>
													</Zoom>
												)}
											</>
										)}
								</Box>
							);
						})}
						{isTyping && (
							<Fade in={isTyping} timeout={300}>
								<Box role="status" sx={{ display: 'flex', alignItems: 'center', ml: 1, mb: 1 }}>
									<Box
										sx={{
											display: 'flex',
											transformOrigin: 'center',
											animation: 'anaBreathe 1.6s ease-in-out infinite',
											'@keyframes anaBreathe': {
												'0%, 100%': { transform: 'scale(0.85)', opacity: 0.85 },
												'50%': { transform: 'scale(1.12)', opacity: 1 }
											}
										}}
									>
										<AnaAvatar size={36} animated />
									</Box>
								</Box>
							</Fade>
						)}
						<div ref={messagesEndRef} />
					</Box>

					<Box
						sx={{
							backgroundColor: (theme) =>
								theme.palette.mode === 'dark' ? 'rgba(0, 30, 69, 0.9)' : 'rgba(255, 255, 255, 0.9)',
							borderTop: (theme) =>
								theme.palette.mode === 'dark'
									? '1px solid rgba(255, 255, 255, 0.08)'
									: '1px solid rgba(0, 0, 0, 0.05)',
							p: isMobile ? 0.75 : 1
						}}
					>
						<AIAssistantInput
							input={input}
							handleInputChange={handleInputChange}
							handleKeyPress={handleKeyPress}
							handleSendMessage={() => handleSendMessage({ starterMessage: input })}
							disabled={isGenerating}
							isMobile={isMobile}
							isXs={isXs}
						/>
					</Box>
				</Box>
			</Fade>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			fullScreen={isMobile}
			TransitionComponent={Fade}
			TransitionProps={{ timeout: 300 }}
			PaperProps={{
				sx: {
					borderRadius: isMobile ? 0 : 8,
					height: isMobile ? '100dvh' : '100vh',
					// fallback for browsers without dvh support
					'@supports not (height: 100dvh)': {
						height: isMobile ? 'calc(100vh - env(safe-area-inset-bottom, 0px))' : '100vh'
					},
					position: isMobile ? 'fixed' : 'fixed',
					right: isMobile ? 0 : 16,
					left: isMobile ? 0 : 'auto',
					top: isMobile ? 0 : 'auto',
					bottom: isMobile ? 0 : 'auto',
					m: 0,
					width: isMobile ? '100vw' : 650,
					maxWidth: '100%',
					zIndex: 1300,
					overflow: 'hidden',
					background: (theme) =>
						theme.palette.mode === 'dark'
							? isXs
								? 'linear-gradient(135deg, rgba(0,15,35,0.98) 0%, rgba(0,30,69,0.98) 100%)'
								: 'linear-gradient(135deg, rgba(0,15,35,0.95) 0%, rgba(0,30,69,0.95) 100%)'
							: isXs
							? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,245,255,0.98) 100%)'
							: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,245,255,0.95) 100%)',
					boxShadow: isXs
						? '0 4px 16px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 123, 255, 0.03)'
						: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 123, 255, 0.05)'
				}
			}}
		>
			{/* Water Animation Background */}
			<WaterAnimation />

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					p: isMobile ? 1.5 : 2,
					position: 'relative',
					zIndex: 1,
					borderBottom: (theme) =>
						theme.palette.mode === 'dark'
							? '1px solid rgba(255, 255, 255, 0.08)'
							: '1px solid rgba(0, 0, 0, 0.05)',
					backgroundColor: (theme) =>
						theme.palette.mode === 'dark' ? 'rgba(0, 30, 69, 0.7)' : 'rgba(255, 255, 255, 0.7)'
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<PulsingAvatar
						src="/ana-icon.png"
						sx={{
							mr: isXs ? 0.5 : isMobile ? 1 : 2,
							width: isXs ? 35 : isMobile ? 40 : 45,
							height: isXs ? 35 : isMobile ? 40 : 45,
							border: '2px solid rgba(33, 150, 243, 0.3)'
						}}
					>
						<SmartToyOutlinedIcon />
					</PulsingAvatar>
					<Box pl={isXs ? 0.25 : isMobile ? 0.5 : 1.5}>
						<Typography
							variant="h6"
							color="primary.main"
							fontWeight={700}
							sx={{ lineHeight: 1.2, fontSize: isXs ? '1rem' : 'inherit' }}
						>
							{t('pages.aiAssistant.ana')}
						</Typography>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontSize: isXs ? '0.7rem' : 'inherit' }}
						>
							{t('pages.aiAssistant.aiAssistant')}
						</Typography>
					</Box>
				</Box>
				<Tooltip title="Close">
					<IconButton
						edge="end"
						color="inherit"
						onClick={onClose}
						aria-label="close"
						sx={{
							p: isMobile ? 0.5 : 1,
							'&:hover': {
								backgroundColor: 'rgba(0, 0, 0, 0.04)'
							}
						}}
					>
						<CloseIcon sx={{ fontSize: isMobile ? 22 : 24 }} />
					</IconButton>
				</Tooltip>
			</Box>

			<DialogContent
				sx={{
					p: 0,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					position: 'relative',
					zIndex: 1,
					backdropFilter: 'blur(5px)',
					height: isMobile ? 'calc(100% - 73px)' : 'auto'
				}}
			>
				{renderConversationsList()}
				{renderChatInterface()}
			</DialogContent>

			{/* Conversation Actions Menu */}
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleMenuClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left'
				}}
				style={{
					marginLeft: -10
				}}
				slotProps={{
					paper: {
						elevation: 3,
						sx: {
							mt: 0.5,
							width: 'auto',
							borderRadius: 2,
							overflow: 'hidden',
							border: '1px solid rgba(0, 0, 0, 0.08)',
							'& .MuiMenuItem-root': {
								fontSize: '0.9rem',
								py: 1.2
							}
						}
					}
				}}
			>
				<MenuItem
					onClick={handleArchiveConversation}
					sx={{
						gap: 1.5,
						'&:hover': {
							backgroundColor: 'rgba(33, 150, 243, 0.08)'
						}
					}}
				>
					<ArchiveOutlinedIcon fontSize="small" />
					{showArchivedConversations
						? t('pages.aiAssistant.unarchiveConversation')
						: t('pages.aiAssistant.archiveConversation.title')}
				</MenuItem>
				<MenuItem
					onClick={handleDeleteConversation}
					sx={{
						color: 'error.main',
						gap: 1.5,
						'&:hover': {
							backgroundColor: 'rgba(244, 67, 54, 0.08)'
						}
					}}
				>
					<DeleteOutlineIcon fontSize="small" />
					{t('pages.aiAssistant.deleteConversation.title')}
				</MenuItem>
			</Menu>

			{/* Confirmation Dialog */}
			<Dialog
				open={confirmDialogOpen}
				onClose={() => handleConfirmDialogClose(false)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				PaperProps={{
					sx: {
						borderRadius: isXs ? 2 : 3,
						boxShadow: isXs
							? '0 4px 16px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 123, 255, 0.03)'
							: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 123, 255, 0.05)',
						overflow: 'hidden',
						maxWidth: isXs ? '300px' : '400px',
						width: '100%',
						m: isXs ? 2 : 3
					}
				}}
			>
				<DialogTitle
					id="alert-dialog-title"
					sx={{
						pb: isXs ? 0.5 : 1,
						pt: isXs ? 2 : 2.5,
						px: isXs ? 2 : 3,
						fontWeight: 600,
						fontSize: isXs ? '1.1rem' : '1.25rem',
						color: confirmDialogAction === 'delete' ? 'error.main' : 'var(--primary-1)'
					}}
				>
					{confirmDialogAction === 'delete'
						? t('pages.aiAssistant.deleteConversation.title')
						: confirmDialogAction === 'archive'
						? showArchivedConversations
							? t('pages.aiAssistant.unarchiveConversation')
							: t('pages.aiAssistant.archiveConversatio.title')
						: ''}
				</DialogTitle>
				<DialogContent sx={{ px: isXs ? 2 : 3 }}>
					<DialogContentText
						id="alert-dialog-description"
						sx={{
							color: 'text.secondary',
							fontSize: isXs ? '0.85rem' : '0.95rem'
						}}
					>
						{confirmDialogAction === 'delete'
							? t('pages.aiAssistant.deleteConversationConfirmation')
							: confirmDialogAction === 'archive'
							? showArchivedConversations
								? t('pages.aiAssistant.unarchiveConversationConfirmation')
								: t('pages.aiAssistant.archiveConversationConfirmation')
							: ''}
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: isXs ? 2 : 3, pb: isXs ? 2 : 2.5, pt: isXs ? 0.5 : 1 }}>
					<Button
						onClick={() => handleConfirmDialogClose(false)}
						sx={{
							textTransform: 'none',
							fontWeight: 500,
							borderRadius: isXs ? 1.5 : 2,
							px: isXs ? 1.5 : 2,
							fontSize: isXs ? '0.8rem' : '0.875rem'
						}}
					>
						{t('pages.aiAssistant.cancel')}
					</Button>
					<Button
						onClick={() => handleConfirmDialogClose(true)}
						autoFocus
						variant="contained"
						color={confirmDialogAction === 'delete' ? 'error' : 'primary'}
						sx={{
							textTransform: 'none',
							fontWeight: 500,
							borderRadius: isXs ? 1.5 : 2,
							px: isXs ? 1.5 : 2,
							fontSize: isXs ? '0.8rem' : '0.875rem',
							boxShadow:
								confirmDialogAction === 'delete'
									? '0 2px 10px rgba(244, 67, 54, 0.2)'
									: '0 2px 10px rgba(33, 150, 243, 0.2)'
						}}
					>
						{t('pages.aiAssistant.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
};

export default AiAssistant;
